import { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, Car, MessageSquare, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const Booking = () => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    vehicleType: '',
    time: '',
    message: '',
  });

  const services = [
    { value: 'nettoyage-integral', label: 'Nettoyage Intégral — Citadine: 230€ | Berline: 280€ | SUV/Sportif: 330€' },
    { value: 'correction-lustrage', label: 'Correction du Vernis - Lustrage — Citadine: 150€ | Berline: 200€ | SUV/Sportif: 250€' },
    { value: 'correction-polissage', label: 'Correction du Vernis - Polissage & Lustrage — Citadine: 280€ | Berline: 330€ | SUV/Sportif: 380€' },
    { value: 'protection-nano', label: 'Protection du Vernis - Nano (1 an) — Citadine: 80€ | Berline: 130€ | SUV/Sportif: 180€' },
    { value: 'protection-ceramique', label: 'Protection du Vernis - Céramique (5 ans) — Citadine: 270€ | Berline: 320€ | SUV/Sportif: 370€' },
    { value: 'ceramique-jantes', label: 'Céramique jantes avec démontage (100–200€)' },
    { value: 'ceramique-vitres', label: 'Céramique vitres (100–200€)' },
    { value: 'polissage-vitres', label: 'Polissage vitres léger (100–200€)' },
    { value: 'moto-nano', label: 'Moto detailing + Nano (150€)' },
    { value: 'moto-ceramique', label: 'Moto detailing + Céramique (250€)' },
  ];

  const vehicleTypes = [
    { value: 'small', label: 'Petite voiture (Citadine)' },
    { value: 'medium', label: 'Berline moyenne' },
    { value: 'large', label: 'Grande berline / SUV' },
    { value: 'luxury', label: 'Voiture de luxe / Sport' },
    { value: 'motorcycle', label: 'Moto / Scooter' },
  ];

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service || !date || !formData.time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const bookingData = {
        ...formData,
        date: format(date, 'yyyy-MM-dd')
      };

      // Import API function
      const { createBooking } = await import('@/utils/api');
      await createBooking(bookingData);
      
      toast.success('Réservation enregistrée ! Nous vous contacterons bientôt pour confirmation.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        vehicleType: '',
        time: '',
        message: '',
      });
      setDate(undefined);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Erreur lors de la réservation. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-20" data-testid="booking-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
        <div className="relative z-10 text-center text-white px-4">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="text-[#c9a961]" size={24} />
            <span className="text-[#c9a961] font-medium tracking-wider uppercase">Réservation en ligne</span>
          </div>
          <h1 className="hero-title text-5xl md:text-7xl mb-4">Prendre Rendez-vous</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Réservez votre créneau en quelques clics
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-[#f8f7f5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-2xl border-none">
            <CardHeader className="bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] text-white rounded-t-lg">
              <CardTitle className="text-3xl text-center section-title">Formulaire de réservation</CardTitle>
              <p className="text-center text-gray-300 mt-2">
                Remplissez le formulaire ci-dessous et nous vous contacterons pour confirmer votre rendez-vous
              </p>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center space-x-2">
                      <User size={18} className="text-[#c9a961]" />
                      <span>Nom complet *</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Jean Dupont"
                      required
                      data-testid="booking-name-input"
                      className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center space-x-2">
                      <Mail size={18} className="text-[#c9a961]" />
                      <span>Email *</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jean.dupont@email.com"
                      required
                      data-testid="booking-email-input"
                      className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center space-x-2">
                      <Phone size={18} className="text-[#c9a961]" />
                      <span>Téléphone *</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="06 12 34 56 78"
                      required
                      data-testid="booking-phone-input"
                      className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleType" className="flex items-center space-x-2">
                      <Car size={18} className="text-[#c9a961]" />
                      <span>Type de véhicule</span>
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('vehicleType', value)}>
                      <SelectTrigger data-testid="booking-vehicle-select" className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]">
                        <SelectValue placeholder="Sélectionnez le type" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicleTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Service Selection */}
                <div className="space-y-2">
                  <Label htmlFor="service" className="flex items-center space-x-2">
                    <Sparkles size={18} className="text-[#c9a961]" />
                    <span>Service souhaité *</span>
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('service', value)} required>
                    <SelectTrigger data-testid="booking-service-select" className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]">
                      <SelectValue placeholder="Choisissez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.value} value={service.value}>
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="flex items-center space-x-2">
                      <Calendar size={18} className="text-[#c9a961]" />
                      <span>Date souhaitée *</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="booking-date-button"
                          className="w-full justify-start text-left font-normal border-gray-300 hover:border-[#c9a961]"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {date ? format(date, 'PPP', { locale: fr }) : 'Sélectionnez une date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="time" className="flex items-center space-x-2">
                      <Clock size={18} className="text-[#c9a961]" />
                      <span>Heure souhaitée *</span>
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('time', value)} required>
                      <SelectTrigger data-testid="booking-time-select" className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]">
                        <SelectValue placeholder="Choisissez une heure" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="flex items-center space-x-2">
                    <MessageSquare size={18} className="text-[#c9a961]" />
                    <span>Message (optionnel)</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Informations supplémentaires, demandes spéciales..."
                    rows={4}
                    data-testid="booking-message-textarea"
                    className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                  />
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note :</strong> Vos données sont protégées et votre réservation sera confirmée rapidement par notre équipe.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  data-testid="booking-submit-button"
                  className="w-full bg-[#c9a961] hover:bg-[#b8984e] text-white text-lg py-6 rounded-full font-semibold transition-all duration-300 luxury-button"
                >
                  Confirmer la réservation
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Besoin d'aide pour réserver ?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:0650470940"
                className="flex items-center space-x-2 text-[#c9a961] font-semibold hover:text-[#b8984e] transition-colors"
              >
                <Phone size={20} />
                <span>06 50 47 09 40</span>
              </a>
              <span className="hidden sm:block text-gray-400">|</span>
              <a
                href="mailto:ilijasukunda2002@gmail.com"
                className="flex items-center space-x-2 text-[#c9a961] font-semibold hover:text-[#b8984e] transition-colors"
              >
                <Mail size={20} />
                <span>ilijasukunda2002@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
